import { createReducer, on } from '@ngrx/store';
import {
  Template,
  ConfiguredChannel,
  CommunicationActivity,
  RenderedTemplate,
  CommunicationRecipientActivity,
  Communication,
  EverbridgeTemplate
} from 'src/app/modules/management/store/communication-center/communications.model';
import { DocumentSummaryWithEmail } from 'src/app/modules/cop/store/impacted-assets/impacted-assets.model';
import * as sharingActions from './sharing.actions';

export const SharingFeatureKey = 'sharing';

export interface State {
  showShareDocumentDialog: boolean;
  communicationTemplates: Template[];
  selectedTemplate: Template;
  everbridgeTemplates: EverbridgeTemplate[];
  selectedEverbridgeTemplate: EverbridgeTemplate;
  selectedEverbridgeTemplatePreview: EverbridgeTemplate;
  showShareDocumentEverbridgeDialog: boolean;
  configuredChannels: ConfiguredChannel[];
  selectedChannel: ConfiguredChannel;
  communicationTemplatePreview: RenderedTemplate;
  communicationActivity: CommunicationActivity[];
  historicCommunication: {
    communication: Communication;
    channelUId: string;
    channelName: string;
    recipients: CommunicationRecipientActivity[];
  };
  emailSummaries: DocumentSummaryWithEmail[];
  isEmailWorking: boolean;
  err?: any;
  showHistoricCommunicationDialog: boolean;
  showHistoricMassNotificationDialog: boolean;
}

export const initialState: State = {
  showShareDocumentDialog: false,
  communicationTemplates: [],
  selectedTemplate: null,
  everbridgeTemplates: [],
  selectedEverbridgeTemplate: null,
  selectedEverbridgeTemplatePreview: null,
  showShareDocumentEverbridgeDialog: false,
  configuredChannels: [],
  selectedChannel: null,
  communicationTemplatePreview: null,
  communicationActivity: [],
  historicCommunication: {
    communication: null,
    channelUId: null,
    channelName: null,
    recipients: null
  },
  emailSummaries: [],
  isEmailWorking: false,
  err: null,
  showHistoricCommunicationDialog: false,
  showHistoricMassNotificationDialog: false
};

export const reducer = createReducer(
  initialState,
  on(sharingActions.getCommunicationTemplatesFromApiComplete, (state, action) => {
    return {
      ...state,
      communicationTemplates: action?.results?.data
    };
  }),
  on(sharingActions.showShareDocumentDialog, (state, action) => {
    const { selectedChannel, selectedTemplate } = action;
    return {
      ...state,
      showShareDocumentDialog: true,
      selectedChannel,
      selectedTemplate,
      communicationTemplatePreview: null
    };
  }),
  on(sharingActions.hideShareDocumentDialog, (state, action) => {
    return {
      ...state,
      showShareDocumentDialog: false,
      showShareDocumentEverbridgeDialog: false,
      selectedChannel: null,
      selectedTemplate: null
    };
  }),
  on(sharingActions.showShareDocumentEverbridgeDialog, (state, action) => {
    const { selectedChannel, selectedEverbridgeTemplate } = action;
    return {
      ...state,
      showShareDocumentEverbridgeDialog: true,
      selectedChannel,
      selectedEverbridgeTemplate,
      selectedEverbridgeTemplatePreview: null
    };
  }),

  on(sharingActions.getCommunicationTemplatePreviewFromApiComplete, (state, action) => {
    return {
      ...state,
      communicationTemplatePreview: action.renderedTemplate
    };
  }),
  on(sharingActions.getConfiguredChannelsFromApiComplete, (state, action) => {
    return {
      ...state,
      configuredChannels: action?.results?.data
    };
  }),
  on(sharingActions.sendCommunicationWithApiComplete, (state, action) => {
    return {
      ...state,
      showShareDocumentDialog: false,
      showShareDocumentEverbridgeDialog: false,
      selectedChannel: null,
      selectedTemplate: null,
      communicationTemplatePreview: null
    };
  }),
  on(sharingActions.getCommunicationActivityForDocumentComplete, (state, action) => {
    const { data } = action.data;
    return {
      ...state,
      communicationActivity: data
    };
  }),
  on(sharingActions.getHistoricCommunicationFromApiComplete, (state, action) => {
    return {
      ...state,
      historicCommunication: {
        ...state.historicCommunication,
        communication: action.communication,
        channelUId: action.channelUId,
        channelName: action.channelName
      }
    };
  }),
  on(sharingActions.getCommunicationRecipientActivityFromApiComplete, (state, action) => {
    return {
      ...state,
      historicCommunication: {
        ...state.historicCommunication,
        recipients: action.results.data
      }
    };
  }),
  on(sharingActions.setError, (state, action) => {
    return {
      ...state,
      err: action.err
    };
  }),
  on(sharingActions.showCommunicationHistoryDialog, (state, action) => {
    return {
      ...state,
      showHistoricCommunicationDialog: true
    };
  }),
  on(sharingActions.hideCommunicationHistoryDialog, (state, action) => {
    return {
      ...state,
      showHistoricCommunicationDialog: false
    };
  }),
  on(sharingActions.showMassNotificationHistoryDialog, (state, action) => {
    return {
      ...state,
      showHistoricMassNotificationDialog: true
    };
  }),
  on(sharingActions.hideMassNotificationHistoryDialog, (state, action) => {
    return {
      ...state,
      showHistoricMassNotificationDialog: false
    };
  }),
  on(sharingActions.getEverbridgeTemplatesForConfiguredChannelFromApiComplete, (state, action) => {
    return {
      ...state,
      everbridgeTemplates: action?.templates
    };
  }),
  on(sharingActions.replaceEverbridgeTokensFromApiComplete, (state, action) => {
    return {
      ...state,
      selectedEverbridgeTemplatePreview: action.template
    };
  })
);

export const getConfiguredChannels = (state: State) => state?.configuredChannels;

export const getCommunicationTemplates = (state: State) => state?.communicationTemplates;

export const getShowShareDocumentDialog = (state: State) => state?.showShareDocumentDialog;

export const getSelectedChannel = (state: State) => state?.selectedChannel;

export const getSelectedTemplate = (state: State) => state?.selectedTemplate;

export const getCommunicationTemplatePreview = (state: State) => state?.communicationTemplatePreview;

export const getDocumentCommunicationActivity = (state: State) => state?.communicationActivity;

export const getHistoricCommunication = (state: State) => state?.historicCommunication;

export const getEmailSummariesForDocument = (state: State) => state?.emailSummaries;

export const getIsEmailWorking = (state: State) => state?.isEmailWorking;

export const getShowHistoricCommunication = (state: State) => state?.showHistoricCommunicationDialog;

export const getShowShareDocumentEverbridgeDialog = (state: State) => state?.showShareDocumentEverbridgeDialog;

export const getEverbridgeTemplates = (state: State) => state?.everbridgeTemplates;

export const getSelectedEverbridgeTemplatePreview = (state: State) => state?.selectedEverbridgeTemplatePreview;

export const getSelectedEverbridgeTemplate = (state: State) => state?.selectedEverbridgeTemplate;

export const getShowHistoricMassNotification = (state: State) => state?.showHistoricMassNotificationDialog;

